import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const FILE_PAGE_SIZE = 100;


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const getCurrentLanguage = () => {
  const path = window.location.pathname.toLowerCase();
  const htmlLang = document.documentElement.lang?.toLowerCase() || "";
  return path.startsWith("/ar/") || htmlLang.startsWith("ar") ? "ar" : "en";
};

const labels = {
  en: {
    pageTitle: "Open Data",
    home: "home",
    introTitle: "Open data service with accuracy and transparency",
    introDescription:
      "Tarahum Charity Foundation provides open data service with the highest levels of accuracy and transparency for visitors with the aim of developing human beings and enhancing public participation and raising the level of awareness and happiness in society.",
    downloads: "Data Downloads",
    filesTitle: "Available Open Data Files",
    download: "Download",
    arPdf: "AR PDF",
    enPdf: "EN PDF",
    downloadPdf: "Download PDF",
    pdfPreview: "Open Data PDF preview",
    loading: "Loading open data files...",
    error: "Unable to load open data right now. Please try again later.",
    empty: "No open data files are available right now.",
  },
  ar: {
    pageTitle: "البيانات المفتوحة",
    home: "الرئيسية",
    introTitle: "خدمة البيانات المفتوحة بدقة وشفافية",
    introDescription:
      "توفر مؤسسة تراحم الخيرية خدمة البيانات المفتوحة بأعلى مستويات الدقة والشفافية للمتعاملين، بهدف تعزيز المشاركة المجتمعية ورفع مستوى الوعي والسعادة في المجتمع.",
    downloads: "تحميل البيانات",
    filesTitle: "ملفات البيانات المفتوحة المتاحة",
    download: "تحميل",
    arPdf: "ملف عربي",
    enPdf: "ملف إنجليزي",
    downloadPdf: "تحميل الملف",
    pdfPreview: "معاينة ملف البيانات المفتوحة",
    loading: "جاري تحميل ملفات البيانات المفتوحة...",
    error: "تعذر تحميل البيانات المفتوحة حاليا. يرجى المحاولة لاحقا.",
    empty: "لا توجد ملفات بيانات مفتوحة متاحة حاليا.",
  },
};

const pointIcons = ["fa-triangle-exclamation", "fa-user-check", "fa-link"];

const buildMainContent = (item, language) => {
  const source = item || {};
  const read = (field, fallback = "") => stripHtml(source[`${field}_${language}`] || source[`${field}_en`] || fallback);

  return {
    intro: {
      title: read("box1title"),
      description: read("box1description"),
      icon: source.box1icon || "",
      defaultIcon: "fa-chart-simple",
    },
    points: [2, 3, 4].map((boxNumber, index) => ({
      id: `${source.id || "open-point"}-${boxNumber}`,
      title: read(`box${boxNumber}title`),
      description: read(`box${boxNumber}description`),
      icon: source[`box${boxNumber}icon`] || "",
      defaultIcon: pointIcons[index],
    })),
  };
};

const cleanDownloadUrl = (value) => {
  const url = String(value || "").trim();
  if (!url || /\/undefined$/i.test(url) || /\/null$/i.test(url) || /r2\.dev\/$/i.test(url)) return "";
  return url;
};

const mapOpenFile = (item, language) => {
  const title = stripHtml(item[`title_${language}`] || item.title_en || item.title_ar || item.title);
  const subtitle = stripHtml(item[`subtitle_${language}`] || item.subtitle_en || item.subtitle_ar || item.subtitle);

  return {
    id: item.id || item.Id || title,
    title,
    subtitle,
    image: getCloudflareImageUrl(item.ImagePath || item.imagePath || item.image, "w=700,sharpen=1"),
    pdfAr: cleanDownloadUrl(item.pdf_ar),
    pdfEn: cleanDownloadUrl(item.pdf_en),
  };
};

function ApiIcon({ icon, defaultIcon, className = "" }) {
  const image = getCloudflareImageUrl(icon, "w=120,sharpen=1");

  if (image) {
    return <img className={`open-data-api-icon ${className}`.trim()} src={image} alt="" />;
  }

  return <i className={`fa-solid ${defaultIcon} ${className}`.trim()}></i>;
}

export default function OpenData({ language: routeLanguage }) {
  const interactions = useHomeInteractions();
  const language = routeLanguage || getCurrentLanguage();
  const isArabic = language === "ar";
  const text = language === "ar" ? {
    ...labels.ar,
    pageTitle: "البيانات المفتوحة",
    home: "الرئيسية",
    introTitle: "خدمة البيانات المفتوحة بدقة وشفافية",
    introDescription: "توفر مؤسسة تراحم الخيرية خدمة البيانات المفتوحة بأعلى مستويات الدقة والشفافية، بهدف تعزيز المشاركة المجتمعية ورفع مستوى الوعي والسعادة في المجتمع.",
    downloads: "تحميل البيانات",
    filesTitle: "ملفات البيانات المفتوحة المتاحة",
    download: "تحميل",
    arPdf: "ملف عربي",
    enPdf: "ملف إنجليزي",
    downloadPdf: "تحميل الملف",
    pdfPreview: "معاينة ملف البيانات المفتوحة",
    loading: "جارٍ تحميل ملفات البيانات المفتوحة...",
    error: "تعذر تحميل البيانات المفتوحة حالياً. يرجى المحاولة لاحقاً.",
    empty: "لا توجد ملفات بيانات مفتوحة متاحة حالياً.",
  } : labels.en;
  const [mainContent, setMainContent] = useState({ intro: { title: "", description: "", icon: "", defaultIcon: "" }, points: [] });
  const [files, setFiles] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    document.title = `${text.pageTitle} | ${isArabic ? "مؤسسة تراحم الخيرية" : "Tarahum Charity Foundation"}`;
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
      document.body.classList.remove("arabic-layout-page");
    };
  }, [isArabic, text.pageTitle]);

  useEffect(() => {
    const controller = new AbortController();

    const loadOpenData = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const [mainResponse, filesResponse] = await Promise.all([
          fetch(getApiUrl("/api/GetOpenDataMainList.ashx?page=1&pageSize=1"), {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          }),
          fetch(getApiUrl(`/api/GetOpenDataList.ashx?page=1&pageSize=${FILE_PAGE_SIZE}`), {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          }),
        ]);

        if (!mainResponse.ok) throw new Error(`Open data main request failed with ${mainResponse.status}`);
        if (!filesResponse.ok) throw new Error(`Open data files request failed with ${filesResponse.status}`);

        const mainResult = await mainResponse.json();
        const filesResult = await filesResponse.json();
        const mainRows = Array.isArray(mainResult?.data) ? mainResult.data : [];
        const fileRows = Array.isArray(filesResult?.data) ? filesResult.data : [];

        setMainContent(mainRows[0] ? buildMainContent(mainRows[0], language) : { intro: { title: "", description: "", icon: "", defaultIcon: "" }, points: [] });
        setFiles(fileRows.map((item) => mapOpenFile(item, language)).filter((item) => item.title));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load open data", error);
          setLoadError(text.error);
          setMainContent({ intro: { title: "", description: "", icon: "", defaultIcon: "" }, points: [] });
          setFiles([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadOpenData();
    return () => controller.abort();
  }, [language, text.error]);

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{text.pageTitle}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to="/">{text.home}</Link></li>
              <li>/</li>
              <li>{text.pageTitle}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={`open-d-w-1${isArabic ? " arabic-open-data-page" : ""}`} dir={isArabic ? "rtl" : undefined}>
        <div className="container">
          <div className="open-container">
            <div className="open-intro">
              <div className="open-intro-icon">
                <i className="fa-solid fa-chart-simple" />
              </div>

              <div>
                <h2>{mainContent.intro.title}</h2>
                <p>{mainContent.intro.description}</p>
              </div>
            </div>

            <div className="open-points">
              {mainContent.points.map((point) => (
                <div className="open-point" key={point.id}>
                  <ApiIcon icon={point.icon} defaultIcon={point.defaultIcon} />
                  <div>
                    <h3>{point.title}</h3>
                    <p>{point.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="open-section-head">
              <div>
                <div className="open-title-small">
                  <i className="fa-solid fa-file-pdf"></i>
                  {text.downloads}
                </div>
                <h2>{text.filesTitle}</h2>
              </div>
            </div>

            {loadError ? <div className="donation-error">{loadError}</div> : null}
            {isLoading ? <div className="donation-error">{text.loading}</div> : null}

            <div className="open-doc-grid">
              {files.map((file) => (
                <div className="open-doc-card" key={file.id}>
                  <div className="open-doc-visual">
                    <img src={file.image} alt={file.title} />
                  </div>

                  <div className="open-doc-body">
                    <h3>{file.title}</h3> 

                    <div className="open-download-label">
                      <i className="fa-solid fa-download"></i>
                      {file.subtitle}
                    </div>

                    <div className="open-actions">
                      {file.pdfAr ? (
                        <button
                          type="button"
                          className="open-btn ar"
                          onClick={() => setSelectedPdf({ title: file.title, subtitle: file.subtitle, pdf: file.pdfAr, label: text.arPdf })}
                        >
                          {text.arPdf}
                          <i className="fa-solid fa-arrow-down"></i>
                        </button>
                      ) : null}

                      {file.pdfEn ? (
                        <button
                          type="button"
                          className="open-btn en"
                          onClick={() => setSelectedPdf({ title: file.title, subtitle: file.subtitle, pdf: file.pdfEn, label: text.enPdf })}
                        >
                          {text.enPdf}
                          <i className="fa-solid fa-arrow-down"></i>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!isLoading && !loadError && !files.length ? (
              <div className="donation-error">{text.empty}</div>
            ) : null}
          </div>
        </div>
      </section>

      {selectedPdf ? (
        <div className="mag-pdf-modal" role="dialog" aria-modal="true" aria-label={text.pdfPreview} dir={isArabic ? "rtl" : undefined}>
          <button className="mag-pdf-backdrop" type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close PDF preview"} onClick={() => setSelectedPdf(null)}></button>
          <div className="mag-pdf-dialog">
            <div className="mag-pdf-head">
              <div>
                <h3>{selectedPdf.title}</h3>
                {selectedPdf.subtitle ? <p>{selectedPdf.subtitle}</p> : null}
              </div>
              <div className="mag-pdf-actions">
                <a href={selectedPdf.pdf} download target="_blank" rel="noopener noreferrer">
                  {text.downloadPdf}
                </a>
                <button type="button" aria-label={isArabic ? "إغلاق المعاينة" : "Close PDF preview"} onClick={() => setSelectedPdf(null)}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
            <iframe src={selectedPdf.pdf} title={`${selectedPdf.title} ${selectedPdf.label}`}></iframe>
          </div>
        </div>
      ) : null}

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
